import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabaseClient } from '../../services/supabase';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

// Chart.jsコンポーネントを登録
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

function Analytics() {
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // 期間設定（デフォルト：過去6ヶ月〜今月）
  const now = new Date();
  const defaultStartDate = new Date(now.getFullYear(), now.getMonth() - 5, 1);
  const [startYear, setStartYear] = useState(defaultStartDate.getFullYear());
  const [startMonth, setStartMonth] = useState(defaultStartDate.getMonth() + 1);
  const [endYear, setEndYear] = useState(now.getFullYear());
  const [endMonth, setEndMonth] = useState(now.getMonth() + 1);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      // 開始日と終了日を計算
      const startDate = `${startYear}-${String(startMonth).padStart(2, '0')}-01`;
      const endDate = new Date(endYear, endMonth, 0); // 終了月の最終日
      const endDateStr = `${endYear}-${String(endMonth).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;

      // 予約データを取得（すべてのステータス）
      const { data: reservations, error } = await supabaseClient
        .from('reservations')
        .select('reservation_date, adult_count, child_count, status')
        .gte('reservation_date', startDate)
        .lte('reservation_date', endDateStr);

      if (error) throw error;

      // 月別にデータを集計
      const monthlyStats = {};
      
      // 期間内のすべての月を初期化
      let current = new Date(startYear, startMonth - 1, 1);
      const end = new Date(endYear, endMonth - 1, 1);
      
      while (current <= end) {
        const key = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
        monthlyStats[key] = {
          month: key,
          label: `${current.getFullYear()}年${current.getMonth() + 1}月`,
          reservationCount: 0,
          guestCount: 0,
          cancelledCount: 0
        };
        current.setMonth(current.getMonth() + 1);
      }

      // 予約データを集計
      reservations?.forEach(res => {
        const date = new Date(res.reservation_date);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        if (monthlyStats[key]) {
          if (res.status === 'cancelled') {
            // キャンセルされた予約
            monthlyStats[key].cancelledCount += 1;
          } else {
            // 有効な予約（pending, confirmed, visited）
            monthlyStats[key].reservationCount += 1;
            monthlyStats[key].guestCount += (res.adult_count || 0) + (res.child_count || 0);
          }
        }
      });

      // 配列に変換してソート
      const sortedData = Object.values(monthlyStats).sort((a, b) => a.month.localeCompare(b.month));
      setMonthlyData(sortedData);

    } catch (error) {
      console.error('分析データ読み込みエラー:', error);
    } finally {
      setLoading(false);
    }
  }, [startYear, startMonth, endYear, endMonth]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  // Chart.js用のデータを準備
  const chartData = useMemo(() => {
    if (monthlyData.length === 0) return null;

    const labels = monthlyData.map(d => d.label.replace(/^\d+年/, ''));
    const reservationCounts = monthlyData.map(d => d.reservationCount);
    const guestCounts = monthlyData.map(d => d.guestCount);
    const cancelledCounts = monthlyData.map(d => d.cancelledCount);

    return {
      labels,
      reservationCounts,
      guestCounts,
      cancelledCounts,
    };
  }, [monthlyData]);

  // Chart.jsのオプション設定（ダークテーマ対応）
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#fff',
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#444',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#888',
        },
        grid: {
          color: '#333',
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: '#888',
        },
        grid: {
          color: '#333',
        },
      },
    },
  };

  // 予約数グラフのデータ
  const reservationsChartData = useMemo(() => {
    if (!chartData) return null;
    return {
      labels: chartData.labels,
      datasets: [
        {
          label: '予約数',
          data: chartData.reservationCounts,
          backgroundColor: 'rgba(76, 175, 80, 0.8)',
          borderColor: 'rgba(76, 175, 80, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [chartData]);

  // 予約人数グラフのデータ
  const guestsChartData = useMemo(() => {
    if (!chartData) return null;
    return {
      labels: chartData.labels,
      datasets: [
        {
          label: '予約人数',
          data: chartData.guestCounts,
          backgroundColor: 'rgba(33, 150, 243, 0.8)',
          borderColor: 'rgba(33, 150, 243, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [chartData]);

  // キャンセル数グラフのデータ
  const cancelledChartData = useMemo(() => {
    if (!chartData) return null;
    return {
      labels: chartData.labels,
      datasets: [
        {
          label: 'キャンセル数',
          data: chartData.cancelledCounts,
          backgroundColor: 'rgba(244, 67, 54, 0.8)',
          borderColor: 'rgba(244, 67, 54, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [chartData]);

  // 複合グラフのデータ（予約数と予約人数を重ねて表示）
  const combinedChartData = useMemo(() => {
    if (!chartData) return null;
    return {
      labels: chartData.labels,
      datasets: [
        {
          label: '予約数',
          data: chartData.reservationCounts,
          backgroundColor: 'rgba(76, 175, 80, 0.6)',
          borderColor: 'rgba(76, 175, 80, 1)',
          borderWidth: 1,
          yAxisID: 'y',
        },
        {
          label: '予約人数',
          data: chartData.guestCounts,
          backgroundColor: 'rgba(33, 150, 243, 0.6)',
          borderColor: 'rgba(33, 150, 243, 1)',
          borderWidth: 1,
          yAxisID: 'y1',
        },
      ],
    };
  }, [chartData]);

  // 予約数とキャンセル数の比較グラフ
  const reservationCancelledChartData = useMemo(() => {
    if (!chartData) return null;
    return {
      labels: chartData.labels,
      datasets: [
        {
          label: '予約数',
          data: chartData.reservationCounts,
          backgroundColor: 'rgba(76, 175, 80, 0.8)',
          borderColor: 'rgba(76, 175, 80, 1)',
          borderWidth: 1,
        },
        {
          label: 'キャンセル数',
          data: chartData.cancelledCounts,
          backgroundColor: 'rgba(244, 67, 54, 0.8)',
          borderColor: 'rgba(244, 67, 54, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [chartData]);

  // 複合グラフのオプション（2つのY軸を使用）
  const combinedChartOptions = {
    ...chartOptions,
    scales: {
      x: {
        ticks: {
          color: '#888',
        },
        grid: {
          color: '#333',
        },
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        beginAtZero: true,
        ticks: {
          color: '#4CAF50',
        },
        grid: {
          color: '#333',
        },
        title: {
          display: true,
          text: '予約数',
          color: '#4CAF50',
        },
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        beginAtZero: true,
        ticks: {
          color: '#2196F3',
        },
        grid: {
          drawOnChartArea: false,
        },
        title: {
          display: true,
          text: '予約人数',
          color: '#2196F3',
        },
      },
    },
  };

  // 年の選択肢を生成（過去3年〜来年）
  const yearOptions = [];
  for (let y = now.getFullYear() - 3; y <= now.getFullYear() + 1; y++) {
    yearOptions.push(y);
  }

  return (
    <section className="admin-section">
      <div className="container">
        <h2>データ分析</h2>

        {/* 期間選択 */}
        <div className="analytics-filter">
          <div className="filter-row">
            <div className="filter-group">
              <label>開始</label>
              <div className="date-selects">
                <select 
                  value={startYear} 
                  onChange={(e) => setStartYear(parseInt(e.target.value))}
                >
                  {yearOptions.map(y => (
                    <option key={y} value={y}>{y}年</option>
                  ))}
                </select>
                <select 
                  value={startMonth} 
                  onChange={(e) => setStartMonth(parseInt(e.target.value))}
                >
                  {[...Array(12)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}月</option>
                  ))}
                </select>
              </div>
            </div>
            <span className="filter-separator">〜</span>
            <div className="filter-group">
              <label>終了</label>
              <div className="date-selects">
                <select 
                  value={endYear} 
                  onChange={(e) => setEndYear(parseInt(e.target.value))}
                >
                  {yearOptions.map(y => (
                    <option key={y} value={y}>{y}年</option>
                  ))}
                </select>
                <select 
                  value={endMonth} 
                  onChange={(e) => setEndMonth(parseInt(e.target.value))}
                >
                  {[...Array(12)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}月</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <p>読み込み中...</p>
        ) : monthlyData.length === 0 ? (
          <p style={{ color: '#888' }}>データがありません。</p>
        ) : (
          <>
            {/* 予約数グラフ */}
            <div className="chart-section">
              <h3>📊 各月の予約数</h3>
              {reservationsChartData && (
                <div className="chart-container">
                  <Bar data={reservationsChartData} options={chartOptions} />
                </div>
              )}
              <div className="chart-summary">
                <p>期間合計: <strong>{monthlyData.reduce((sum, d) => sum + d.reservationCount, 0)}</strong> 件</p>
              </div>
            </div>

            {/* 予約人数グラフ */}
            <div className="chart-section">
              <h3>👥 各月の予約人数</h3>
              {guestsChartData && (
                <div className="chart-container">
                  <Bar data={guestsChartData} options={chartOptions} />
                </div>
              )}
              <div className="chart-summary">
                <p>期間合計: <strong>{monthlyData.reduce((sum, d) => sum + d.guestCount, 0)}</strong> 名</p>
              </div>
            </div>

            {/* キャンセル数グラフ */}
            <div className="chart-section">
              <h3>❌ 各月のキャンセル数</h3>
              {cancelledChartData && (
                <div className="chart-container">
                  <Bar data={cancelledChartData} options={chartOptions} />
                </div>
              )}
              <div className="chart-summary">
                <p>期間合計: <strong>{monthlyData.reduce((sum, d) => sum + d.cancelledCount, 0)}</strong> 件</p>
              </div>
            </div>

            {/* 予約数とキャンセル数の比較グラフ */}
            <div className="chart-section">
              <h3>📊 予約数とキャンセル数の比較</h3>
              {reservationCancelledChartData && (
                <div className="chart-container">
                  <Bar data={reservationCancelledChartData} options={chartOptions} />
                </div>
              )}
              <div className="chart-summary">
                <p>
                  キャンセル率: <strong>
                    {(() => {
                      const totalReservations = monthlyData.reduce((sum, d) => sum + d.reservationCount + d.cancelledCount, 0);
                      const totalCancelled = monthlyData.reduce((sum, d) => sum + d.cancelledCount, 0);
                      return totalReservations > 0 ? ((totalCancelled / totalReservations) * 100).toFixed(1) : 0;
                    })()}%
                  </strong>
                </p>
              </div>
            </div>

            {/* 複合グラフ（予約数と予約人数を重ねて表示） */}
            <div className="chart-section">
              <h3>📈 予約数と予約人数の比較</h3>
              {combinedChartData && (
                <div className="chart-container">
                  <Bar data={combinedChartData} options={combinedChartOptions} />
                </div>
              )}
            </div>

            {/* データテーブル */}
            <div className="chart-section">
              <h3>📋 月別データ一覧</h3>
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>年月</th>
                    <th>予約数</th>
                    <th>予約人数</th>
                    <th>キャンセル数</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyData.map(data => (
                    <tr key={data.month}>
                      <td>{data.label}</td>
                      <td>{data.reservationCount} 件</td>
                      <td>{data.guestCount} 名</td>
                      <td className="cancelled-cell">{data.cancelledCount} 件</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td><strong>合計</strong></td>
                    <td><strong>{monthlyData.reduce((sum, d) => sum + d.reservationCount, 0)} 件</strong></td>
                    <td><strong>{monthlyData.reduce((sum, d) => sum + d.guestCount, 0)} 名</strong></td>
                    <td className="cancelled-cell"><strong>{monthlyData.reduce((sum, d) => sum + d.cancelledCount, 0)} 件</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </div>

      <style>{`
        .analytics-filter {
          background: #1a1a1a;
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 30px;
        }

        .filter-row {
          display: flex;
          align-items: center;
          gap: 15px;
          flex-wrap: wrap;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .filter-group label {
          color: #888;
          font-size: 0.85rem;
        }

        .date-selects {
          display: flex;
          gap: 8px;
        }

        .date-selects select {
          padding: 8px 12px;
          background: #2a2a2a;
          border: 1px solid #444;
          border-radius: 4px;
          color: #fff;
          font-size: 0.95rem;
        }

        .filter-separator {
          color: #888;
          font-size: 1.2rem;
          margin-top: 20px;
        }

        .chart-section {
          background: #1a1a1a;
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .chart-section h3 {
          color: #fff;
          margin-bottom: 20px;
          font-size: 1.1rem;
        }

        .chart-container {
          height: 400px;
          margin-bottom: 10px;
          position: relative;
        }

        @media (max-width: 768px) {
          .chart-container {
            height: 300px;
          }
        }

        .chart-summary {
          margin-top: 15px;
          padding-top: 15px;
          border-top: 1px solid #333;
          color: #888;
        }

        .chart-summary strong {
          color: #fff;
          font-size: 1.1rem;
        }

        .analytics-table {
          width: 100%;
          border-collapse: collapse;
        }

        .analytics-table th,
        .analytics-table td {
          padding: 12px 15px;
          text-align: left;
          border-bottom: 1px solid #333;
        }

        .analytics-table th {
          background: #2a2a2a;
          color: #fff;
          font-weight: bold;
        }

        .analytics-table td {
          color: #ccc;
        }

        .analytics-table tfoot td {
          background: #2a2a2a;
          color: #fff;
        }

        .analytics-table tbody tr:hover {
          background: #252525;
        }

        .analytics-table .cancelled-cell {
          color: #f44336;
        }

        @media (max-width: 600px) {
          .filter-row {
            flex-direction: column;
            align-items: flex-start;
          }

          .filter-separator {
            margin-top: 0;
          }

          .chart-container {
            height: 250px;
          }
        }
      `}</style>
    </section>
  );
}

export default Analytics;
